'use client';

import CopyLink from '@/components/common/copy-link';
import { ChevronLeftIcon } from '@/components/Icons/Icons';
import MaterialIcons from '@/components/MaterialIcons/materialIcons';
import { formatDate } from '@/helpers/date-format';
import Link from 'next/link';
import React from 'react';

interface AssessmentHeaderProps {
  assessmentName: string;
  learningPathUrl: string;
  updatedAt: string;
  totalQuestions: number;
  questionIndex?: number; // Add questionIndex to show current question number
  isStandaloneCourse?: boolean;
  type?: 'assessment' | 'quiz';
  onToggleQuestionCard?: () => void;
  isQuestionCardOpen?: boolean;
  /** Renders inline after “Released” in the stats row (e.g. Markdown Ask AI). */
  markdownToolbar?: React.ReactNode;
}

export function AssessmentHeader({
  assessmentName,
  learningPathUrl,
  updatedAt,
  totalQuestions,
  questionIndex,
  isStandaloneCourse = false,
  type = 'quiz',
  onToggleQuestionCard,
  isQuestionCardOpen = true,
  markdownToolbar,
}: AssessmentHeaderProps) {
  // Build back link URL
  const backUrl = isStandaloneCourse
    ? `/courses${learningPathUrl}`
    : `/learning-paths${learningPathUrl}`;

  // Back link text based on context (matching Academy 1.0)
  const backLinkText = isStandaloneCourse
    ? 'Back to course'
    : 'Back to learning path';

  const typeLabel = type === 'assessment' ? 'Assessment' : 'Quiz';
  const titleSuffix = type === 'assessment' ? 'Assessment' : 'Quiz';

  return (
    <div className="bg-surface-3 rounded-lg p-8 flex flex-col gap-[2px] md:rounded-xl">
      {/* Back link */}
      <Link
        href={backUrl}
        className="additional-type-s-button text-text-action-medium hover:text-text-action-strong flex items-center gap-2 w-fit"
      >
        <ChevronLeftIcon size={12} />
        {backLinkText}
      </Link>

       {/* Title and Metadata */}
       <div className="flex flex-col gap-1">
        <h1 className="type-h3 lg:h1-compact select-none">
          {assessmentName} {titleSuffix}
        </h1>
        <div className="text-text-default-strong select-none flex flex-row flex-wrap gap-2 items-center justify-between gap-y-3">
          <div className="flex min-w-0 flex-row flex-wrap items-center gap-x-2 gap-y-2">
            <p className="cs-caption m-0">{typeLabel}</p>
            <span className="cs-caption">|</span>
            <p className="cs-caption m-0">
              {questionIndex !== undefined 
                ? `Question ${questionIndex + 1} / ${totalQuestions}`
                : `${totalQuestions}/${totalQuestions} Questions`}
            </p>
            <span className="cs-caption hidden lg:inline">|</span>
            <p className="cs-caption m-0 hidden lg:block">
              Released: {formatDate(updatedAt)}
            </p>
            {markdownToolbar ? (
              <>
                <span className="cs-caption hidden lg:inline">|</span>
                <div className="flex min-w-0 shrink-0 items-center">{markdownToolbar}</div>
              </>
            ) : null}
          </div>
          {/* Copy Link - Desktop Only */}
          <div className="hidden lg:block">
            <CopyLink iconClassName="w-5 h-5 text-text-default-medium hover:text-text-default-strong" />
          </div>
          {/* Kebab Menu Toggle - Mobile & Tablet Only */}
          {onToggleQuestionCard && (
            <button
              onClick={onToggleQuestionCard}
              className="lg:hidden outline-focussed p-1 flex items-center justify-center"
              aria-label={isQuestionCardOpen ? 'Hide question' : 'Show question'}
              aria-expanded={isQuestionCardOpen}
            >
              <MaterialIcons
                iconName="more_horiz"
                className="text-icon-subtle hover:text-text-default-strong !text-[20px] !leading-none"
              />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

